import { useEffect, useRef } from 'react';

import { chime } from './chime';

/**
 * Sound and a tab-title count, driven off an unread number.
 *
 * ── WHY IT ONLY FIRES ON AN INCREASE ────────────────────────────────────────
 *
 * The obvious version plays a sound whenever the count is above zero, and
 * then chimes on every page load, every poll and every tab switch for
 * somebody who has three unread notifications they are deliberately ignoring.
 * Within a day they mute it, and the one that mattered is lost with the rest.
 *
 * So it fires when the number goes UP, and never on the first reading — the
 * first reading is history, not news. Arriving at a screen with nine unread
 * is silent; the tenth makes a sound.
 *
 * ── AND WHY THE TAB TITLE TOO ───────────────────────────────────────────────
 *
 * The sound is missed by anybody with their volume down, and the badge is
 * invisible while the tab is in the background — which is where a support
 * desk's tab spends most of its day. "(3) ChiCare" in the title is the one
 * signal that survives both.
 *
 * It is restored on unmount, so a screen that stops counting does not leave
 * the number stuck in the title.
 */
export function useAlerts(unread: number, enabled = true): void {
  // undefined means "nothing seen yet", which is different from zero: a first
  // reading of zero must not count as a decrease, and a first reading of nine
  // must not count as nine arrivals.
  const seen = useRef<number | undefined>(undefined);

  useEffect(() => {
    if (!enabled) return;

    const previous = seen.current;

    seen.current = unread;

    if (previous === undefined) return;

    if (unread > previous) chime();
  }, [unread, enabled]);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') return;

    const original = document.title;

    // Strip any count a previous render left, so switching between screens
    // cannot produce "(2) (3) ChiCare".
    const base = original.replace(/^\(\d+\)\s*/, '');

    document.title = unread > 0 ? `(${unread}) ${base}` : base;

    return () => {
      document.title = base;
    };
  }, [unread, enabled]);
}
